/**
 * useKanbanSelection — multi-select state for the kanban.
 *
 * Per FWV v8 §30: state machine extracted from TodayView into its own hook
 * so the rendering surface stays focused on layout. Shift/cmd/ctrl-click
 * toggles in selection; plain click on a card advances OR replaces selection
 * if a non-empty selection exists.
 */
import { useState, useCallback, useEffect } from 'react'

export type SelectionState = {
  ids: string[]
  has: (id: string) => boolean
  toggle: (id: string) => void
  clear: () => void
  replace: (ids: string[]) => void
}

export function useKanbanSelection(): SelectionState {
  const [ids, setIds] = useState<string[]>([])

  const has = useCallback((id: string) => ids.includes(id), [ids])

  const toggle = useCallback((id: string) => {
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const clear = useCallback(() => setIds([]), [])

  const replace = useCallback((next: string[]) => setIds(next), [])

  // Escape clears selection
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIds([])
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return { ids, has, toggle, clear, replace }
}

/**
 * Determine what a plain click on a card should do, given the current selection.
 *  - shift/cmd/ctrl held → caller invokes toggle() and returns
 *  - no modifier, no selection → caller advances the card
 *  - no modifier, has selection, card NOT in selection → caller replaces with [this card]
 *  - no modifier, has selection, card IN selection → caller advances the card
 */
export function onCardClickLogic(
  e: React.MouseEvent,
  taskId: string,
  selection: SelectionState,
  onAdvance: () => void
): 'toggle' | 'advance' | 'replace' {
  if (e.shiftKey || e.metaKey || e.ctrlKey) {
    e.preventDefault()
    return 'toggle'
  }
  if (selection.ids.length > 0 && !selection.has(taskId)) {
    return 'replace'
  }
  // Plain click on a selected OR unselected card with no selection → advance
  onAdvance()
  return 'advance'
}